import { useEffect, useMemo, useState } from 'react';
import { Wrench, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useActiveSupportNotices } from '@/hooks/useSupportNotices';
import { SupportNotice } from '@/lib/supportNotices';

interface SupportNoticeBannerProps {
  notices?: SupportNotice[];
  isLoading?: boolean;
  errorMessage?: string | null;
}

type SupportNoticeDismissalScope = 'portal' | 'product';

export const SUPPORT_NOTICE_STRIP_CLASS = 'border-b border-amber-200/70 bg-amber-50/90 px-4 py-2 dark:border-amber-500/25 dark:bg-amber-950/20 sm:px-6';
export const SUPPORT_NOTICE_CARD_CLASS = 'border-amber-300/80 bg-white/80 py-2 text-amber-950 shadow-sm [&>svg]:text-amber-600 dark:border-amber-500/40 dark:bg-slate-950/80 dark:text-amber-100 dark:[&>svg]:text-amber-200';
export const SUPPORT_NOTICE_ICON_CLASS = 'h-4 w-4';
export const SUPPORT_NOTICE_CLOSE_BUTTON_CLASS = 'absolute right-3 rounded-md p-1 text-amber-600 transition hover:bg-amber-50 hover:text-amber-800 dark:text-amber-200 dark:hover:bg-amber-500/10 dark:hover:text-amber-50';
export const SUPPORT_NOTICE_COPY_CLASS = 'text-amber-950 dark:text-amber-100';

function getDismissedSupportNoticesStorageKey(scope: SupportNoticeDismissalScope) {
  return `dismissed-support-notices:${scope}`;
}

function normalizeSupportNoticeMarkdown(message: string) {
  const lines = message.replace(/\r\n/g, '\n').split('\n');

  return lines
    .map((line, index) => {
      if (!line.trim()) {
        return line;
      }

      const nextLine = lines[index + 1];
      return nextLine && nextLine.trim() ? `${line}  ` : line;
    })
    .join('\n');
}

function getSanitizedSupportNoticeUrl(href?: string) {
  if (!href) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(href, 'https://support-notice.local');
    return ['http:', 'https:', 'mailto:'].includes(parsedUrl.protocol) ? href : undefined;
  } catch {
    return undefined;
  }
}

function shouldUseCompactSupportNoticeLayout(message: string) {
  const normalizedMessage = message.trim();
  const lines = normalizedMessage.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  return (
    normalizedMessage.length > 0 &&
    lines.length === 1 &&
    !/^(#{1,6}\s|>\s|[-*+]\s|\d+\.\s|```)/.test(lines[0] ?? '') &&
    normalizedMessage.length <= 120
  );
}

export function SupportNoticeMessage({
  message,
  className = '',
}: {
  message: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="m-0">{children}</p>,
          ul: ({ children }) => <ul className="my-1 list-disc pl-4">{children}</ul>,
          ol: ({ children }) => <ol className="my-1 list-decimal pl-4">{children}</ol>,
          li: ({ children }) => <li className="my-0.5">{children}</li>,
          a: ({ href, children }) => (
            getSanitizedSupportNoticeUrl(href) ? (
              <a
                href={getSanitizedSupportNoticeUrl(href)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                {children}
              </a>
            ) : (
              <span>{children}</span>
            )
          ),
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => <code className="rounded bg-amber-100/70 px-1 py-0.5 text-[0.85em] dark:bg-amber-500/10">{children}</code>,
        }}
      >
        {normalizeSupportNoticeMarkdown(message)}
      </ReactMarkdown>
    </div>
  );
}

export function useDismissedSupportNotices(
  notices?: SupportNotice[],
  scope: SupportNoticeDismissalScope = 'product',
) {
  const [dismissedNoticeIds, setDismissedNoticeIds] = useState<string[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(getDismissedSupportNoticesStorageKey(scope));
      setDismissedNoticeIds(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setDismissedNoticeIds([]);
    }
  }, [scope]);

  const visibleNotices = useMemo(
    () => (notices ?? []).filter((notice) => !dismissedNoticeIds.includes(notice.id)),
    [dismissedNoticeIds, notices],
  );

  const dismissNotice = (noticeId: string) => {
    setDismissedNoticeIds((current) => {
      const next = current.includes(noticeId) ? current : [...current, noticeId];
      localStorage.setItem(getDismissedSupportNoticesStorageKey(scope), JSON.stringify(next));
      return next;
    });
  };

  return {
    visibleNotices,
    dismissNotice,
  };
}

function SupportNoticeStack({
  notices,
  onDismiss,
}: {
  notices: SupportNotice[];
  onDismiss: (noticeId: string) => void;
}) {
  if (!notices.length) {
    return null;
  }

  return (
    <div
      className={SUPPORT_NOTICE_STRIP_CLASS}
      data-testid="support-notice-banner"
    >
      <div className="space-y-2">
        {notices.map((notice) => {
          const isCompact = shouldUseCompactSupportNoticeLayout(notice.message);

          return (
            <Alert
              key={notice.id}
              className={`${SUPPORT_NOTICE_CARD_CLASS} ${
                isCompact ? '[&>svg]:top-1/2 [&>svg]:-translate-y-1/2' : '[&>svg]:top-4 [&>svg]:translate-y-0'
              }`}
            >
              <Wrench className={SUPPORT_NOTICE_ICON_CLASS} />
              <button
                type="button"
                aria-label={`Dismiss support notice ${notice.message}`}
                className={`${SUPPORT_NOTICE_CLOSE_BUTTON_CLASS} ${
                  isCompact ? 'top-1/2 -translate-y-1/2' : 'top-3'
                }`}
                onClick={() => onDismiss(notice.id)}
              >
                <X className="h-4 w-4" />
              </button>
              <AlertDescription className={`pr-10 ${SUPPORT_NOTICE_COPY_CLASS}`}>
                <SupportNoticeMessage
                  message={notice.message}
                  className={`min-w-0 flex-1 overflow-hidden break-words text-sm [&_ul]:my-0 [&_ol]:my-0 ${
                    isCompact ? '[&_p]:m-0 [&_p]:flex [&_p]:min-h-5 [&_p]:items-center [&_p]:truncate' : ''
                  }`}
                />
              </AlertDescription>
            </Alert>
          );
        })}
      </div>
    </div>
  );
}

export function SupportNoticeBanner({
  notices,
  isLoading = false,
  errorMessage = null,
}: SupportNoticeBannerProps) {
  const { visibleNotices, dismissNotice } = useDismissedSupportNotices(notices, 'product');

  if (isLoading || errorMessage || !visibleNotices.length) {
    return null;
  }

  return <SupportNoticeStack notices={visibleNotices} onDismiss={dismissNotice} />;
}

export function useActiveSupportNoticeBannerState() {
  const { data, isLoading, error } = useActiveSupportNotices('workspace');
  const { visibleNotices, dismissNotice } = useDismissedSupportNotices(data ?? [], 'product');

  return {
    visibleNotices,
    dismissNotice,
    isLoading,
    errorMessage: error instanceof Error ? error.message : null,
  };
}

export function ActiveSupportNoticeBanner() {
  const { visibleNotices, dismissNotice, isLoading, errorMessage } = useActiveSupportNoticeBannerState();

  if (isLoading || errorMessage || !visibleNotices.length) {
    return null;
  }

  return <SupportNoticeStack notices={visibleNotices} onDismiss={dismissNotice} />;
}
